import { useState, useEffect, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Crown, MagnifyingGlass, WhatsappLogo, FilePdf, FileCsv, Lightning, Target, ChartLineUp, TrendUp, TrendDown, CaretDown, Info, Clock } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level animation configs — stable references avoid re-render churn
const PAGE_INITIAL = { opacity: 0 };
const PAGE_ANIMATE = { opacity: 1 };

/**
 * Small chip next to each ELO number showing the current tournament delta
 * (elo_rating − baseline_elo). Hidden for players without a baseline set
 * or when delta is exactly 0 (no movement → nothing to show).
 */
function EloDeltaArrow({ delta }) {
  if (delta == null) return null;
  const up = delta > 0;
  const down = delta < 0;
  if (!up && !down) return null;
  const Icon = up ? TrendUp : TrendDown;
  const colour = up ? "text-primary bg-primary/10" : "text-destructive bg-destructive/10";
  const sign = up ? "+" : "";
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${colour}`}
      data-testid="elo-delta-arrow"
      title={`Current tournament delta: ${sign}${delta}`}
    >
      <Icon className="w-2.5 h-2.5" weight="bold" />
      {sign}{delta}
    </span>
  );
}

/**
 * Expandable dropdown under a player row in the ELO Rankings tab. Lazy-fetches
 * the player's match history and renders the last 3 completed matches with
 * opponent, score, ELO delta, and match points earned — so members can see
 * *why* someone's ELO is where it is.
 */
function RecentEloMatchesRow({ playerId, colSpan }) {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/players/${playerId}/match-history`)
      .then((r) => {
        if (cancelled) return;
        const completed = (r.data || []).filter(
          (m) => m.status === "completed" || m.status === "walkover"
        );
        setMatches(completed.slice(0, 3));
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playerId]);

  const renderBody = () => {
    if (loading) {
      return <p className="text-xs text-muted-foreground py-3 text-center">Loading recent matches…</p>;
    }
    if (error) {
      return <p className="text-xs text-destructive py-3 text-center">Failed to load match history</p>;
    }
    if (!matches || matches.length === 0) {
      return <p className="text-xs text-muted-foreground py-3 text-center italic">No completed matches yet.</p>;
    }
    return (
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Last {matches.length} Match{matches.length === 1 ? "" : "es"}
        </p>
        {matches.map((m) => {
          const isP1 = m.player1_id === playerId;
          const opp = isP1 ? m.player2_name : m.player1_name;
          const myScore = isP1 ? m.player1_score : m.player2_score;
          const oppScore = isP1 ? m.player2_score : m.player1_score;
          const won = m.winner_id === playerId;
          const eloDelta = won ? m.elo_change_winner : m.elo_change_loser;
          const pts = m.my_match_points;
          const date = (m.played_at || m.scheduled_date || "").slice(0, 10);
          return (
            <Link
              key={m.id}
              to={`/live/${m.id}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 rounded-md bg-background/50 hover:bg-background border border-border/50 hover:border-primary/40 transition-colors text-xs"
              data-testid={`elo-recent-match-${m.id}`}
            >
              <Badge
                variant="outline"
                className={`text-[10px] w-7 justify-center shrink-0 ${won ? "border-primary/40 text-primary" : "border-destructive/40 text-destructive"}`}
              >
                {won ? "W" : "L"}
              </Badge>
              <span className="text-muted-foreground shrink-0">{date}</span>
              <span className="flex-1 min-w-0 truncate">
                vs <span className="font-medium text-foreground">{opp || "Unknown"}</span>
              </span>
              <span className="font-heading font-bold tabular-nums shrink-0">
                {myScore}–{oppScore}
              </span>
              <span className="flex items-center gap-2 shrink-0 ml-auto">
                {eloDelta != null && (
                  <span className={`tabular-nums font-semibold inline-flex items-center gap-0.5 ${eloDelta > 0 ? "text-primary" : eloDelta < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                    {eloDelta > 0 ? <TrendUp className="w-3 h-3" weight="bold" /> : eloDelta < 0 ? <TrendDown className="w-3 h-3" weight="bold" /> : null}
                    {eloDelta > 0 ? "+" : ""}{eloDelta} ELO
                  </span>
                )}
                {pts != null && (
                  <span className="tabular-nums text-secondary font-semibold">
                    +{pts} pts
                  </span>
                )}
              </span>
            </Link>
          );
        })}
        <div className="pt-1">
          <Link
            to={`/players/${playerId}`}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
            data-testid="elo-recent-view-profile"
          >
            View full profile →
          </Link>
        </div>
      </div>
    );
  };

  return (
    <TableRow className="border-border bg-muted/5 hover:bg-muted/5" data-testid={`elo-recent-row-${playerId}`}>
      <TableCell colSpan={colSpan} className="py-3 px-6">
        {renderBody()}
      </TableCell>
    </TableRow>
  );
}

export default function Rankings() {
  const { user } = useAuth();
  const [allRankings, setAllRankings] = useState([]);
  const [premierRankings, setPremierRankings] = useState([]);
  const [challengerRankings, setChallengerRankings] = useState([]);
  const [eloRankings, setEloRankings] = useState([]);
  const [matchPointsLeaderboard, setMatchPointsLeaderboard] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchRankings = useCallback(async () => {
    try {
      const [allRes, premierRes, challengerRes, eloRes, mpRes] = await Promise.all([
        axios.get(`${API}/rankings`),
        axios.get(`${API}/rankings/premier`),
        axios.get(`${API}/rankings/challenger`),
        axios.get(`${API}/elo-rankings`).catch(() => ({ data: [] })),
        axios.get(`${API}/match-points/leaderboard`).catch(() => ({ data: [] }))
      ]);
      setAllRankings(allRes.data);
      setPremierRankings(premierRes.data);
      setChallengerRankings(challengerRes.data);
      setEloRankings(eloRes.data);
      setMatchPointsLeaderboard(mpRes.data);
    } catch (error) {
      console.error("Error fetching rankings:", error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchRankings(); }, [fetchRankings]);

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

  /** Running total = historical placement points + live per-match points from
   * the currently-running tournament. Used for display sort-order on the
   * Club Rankings tab so the table stays meaningful during an ongoing event. */
  const livePoints = (p) => (p.total_points || 0) + (p.match_points_total || 0);

  /** Sort by live points desc (falling back to the server-computed `ranking`
   * as a deterministic tiebreaker when two players have identical totals),
   * then re-stamp `ranking` to match the new order so the "Club Rank" column
   * reflects what the user actually sees.  */
  const sortAndRank = (list) => list
    .slice()
    .sort((a, b) => livePoints(b) - livePoints(a) || (a.ranking ?? 999) - (b.ranking ?? 999))
    .map((p, i) => ({ ...p, ranking: i + 1 }));

  const filteredRankings = sortAndRank(
    allRankings.filter((p) => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const sortedAllRankings = sortAndRank(allRankings);
  const sortedPremierRankings = sortAndRank(premierRankings);
  const sortedChallengerRankings = sortAndRank(challengerRankings);

  /**
   * Streams a server-side export directly to a download. Same UX pattern as
   * the Tournament Report PDF — fetches blob bytes, creates a hidden anchor,
   * triggers `.click()`, no new tabs. Used for both rankings PDF and CSV
   * (and any future export added to this page).
   *
   * @param {"pdf" | "csv"} kind
   */
  const downloadRankingsExport = async (kind) => {
    const tid = toast.loading(`Preparing rankings ${kind.toUpperCase()}…`);
    try {
      const res = await axios.get(`${API}/export/rankings/${kind}`, { responseType: "blob" });
      const ct = res.headers["content-type"] || (kind === "pdf" ? "application/pdf" : "text/csv");
      const url = URL.createObjectURL(new Blob([res.data], { type: ct }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rankings.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Rankings ${kind.toUpperCase()} downloaded`, { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to download ${kind}`, { id: tid });
    }
  };

  const handleExportPDF = () => downloadRankingsExport("pdf");
  const handleExportCSV = () => downloadRankingsExport("csv");

  const handleWhatsAppShare = async () => {
    try {
      const res = await axios.get(`${API}/share/rankings`);
      window.open(res.data.whatsapp_url, '_blank');
    } catch {
      const text = encodeURIComponent("Check out the Annadil Burhani DSM Snooker Rankings!");
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }
  };

  // Build form: use recent_form (last 5 results as W/L/D array)
  const getForm = (player) => {
    return player.recent_form || [];
  };

  const RankingTable = ({ players, showTier = true }) => (
    <Table>
      <TableHeader>
        <TableRow className="border-border hover:bg-transparent">
          <TableHead className="w-16 text-muted-foreground">Club Rank</TableHead>
          <TableHead className="text-muted-foreground">Player</TableHead>
          {showTier && <TableHead className="text-muted-foreground">Tier</TableHead>}
          <TableHead className="text-right text-muted-foreground">Points</TableHead>
          <TableHead className="text-right text-muted-foreground">High Break</TableHead>
          <TableHead className="text-center text-muted-foreground">Form</TableHead>
          <TableHead className="text-right text-muted-foreground">Win %</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {players.map((player) => {
          const winRate = player.matches_played > 0 
            ? ((player.matches_won / player.matches_played) * 100).toFixed(1) 
            : 0;
          const form = getForm(player);
          // Live total = placement points (past tournament finishes) + match points
          // accrued during the currently-running tournament. Keeps this column
          // meaningful while a tournament is still in progress; `total_points`
          // alone stays at 0 until the tournament closes and placements are written.
          const placementPts = player.total_points || 0;
          const livePts = player.match_points_total || 0;
          const pointsForDisplay = placementPts + livePts;
          
          return (
            <TableRow 
              key={player.id} 
              className={`border-border hover:bg-white/5 ranking-row ${player.tier}`}
              data-testid={`ranking-table-row-${player.ranking}`}
            >
              <TableCell>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  player.ranking === 1 ? 'rank-1' : 
                  player.ranking === 2 ? 'rank-2' : 
                  player.ranking === 3 ? 'rank-3' : 
                  'bg-muted text-muted-foreground'
                }`}>
                  {player.ranking}
                </div>
              </TableCell>
              <TableCell>
                <Link 
                  to={`/players/${player.id}`} 
                  className="flex items-center gap-3 hover:text-primary transition-colors"
                >
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarImage src={player.photo_url} alt={player.name} />
                    <AvatarFallback className="bg-muted text-muted-foreground text-sm">
                      {getInitials(player.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{player.name}</p>
                    <p className="text-xs text-muted-foreground">{player.nickname || 'Player'}</p>
                  </div>
                </Link>
              </TableCell>
              {showTier && (
                <TableCell>
                  <Badge className={player.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}>
                    {player.tier === 'premier' ? 'Premier' : 'Challenger'}
                  </Badge>
                </TableCell>
              )}
              <TableCell className="text-right">
                <div className="inline-flex items-center justify-end gap-1.5" data-testid={`club-points-${player.id}`}>
                  <span className="font-heading font-bold text-primary tabular-nums">{pointsForDisplay}</span>
                  {livePts > 0 && (
                    <span
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-secondary/15 text-secondary"
                      title={`${placementPts} placement · +${livePts} live from ongoing tournament`}
                    >
                      +{livePts} live
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right font-medium text-secondary">{player.highest_break || 0}</TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-0.5 text-xs font-bold">
                  {form.length > 0 ? form.map((r, i) => (
                    <span key={`form-${player.id}-${i}`} className={`w-6 h-6 rounded flex items-center justify-center ${
                      r === 'W' ? 'bg-primary/15 text-primary' :
                      r === 'L' ? 'bg-destructive/15 text-destructive' :
                      'bg-muted text-muted-foreground'
                    }`}>{r}</span>
                  )) : <span className="text-muted-foreground">-</span>}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <span className={winRate >= 50 ? 'text-primary' : 'text-muted-foreground'}>
                  {winRate}%
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  const EloTable = ({ players }) => {
    const [expandedId, setExpandedId] = useState(null);
    const toggle = (pid) => setExpandedId((cur) => (cur === pid ? null : pid));
    return (
      <Table>
        <TableHeader>
          <TableRow className="border-border hover:bg-transparent">
            <TableHead className="w-12" />
            <TableHead className="w-16 text-muted-foreground">#</TableHead>
            <TableHead className="text-muted-foreground">Player</TableHead>
            <TableHead className="text-right text-muted-foreground">ELO Rating</TableHead>
            <TableHead className="text-right text-muted-foreground">Club Rank</TableHead>
            <TableHead className="text-center text-muted-foreground">Matches</TableHead>
            <TableHead className="text-center text-muted-foreground">Streak</TableHead>
            <TableHead className="text-right text-muted-foreground">Win %</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {players.map((player) => {
            const winRate = player.matches_played > 0 ? ((player.matches_won / player.matches_played) * 100).toFixed(1) : 0;
            const elo = player.elo_rating || 1500;
            const isOpen = expandedId === player.id;
            return (
              <Fragment key={player.id}>
                <TableRow
                  className={`border-border hover:bg-white/5 cursor-pointer ${isOpen ? "bg-white/[0.03]" : ""}`}
                  onClick={() => toggle(player.id)}
                  data-testid={`elo-ranking-row-${player.elo_rank}`}
                >
                  <TableCell className="pr-0">
                    <CaretDown
                      className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180 text-primary" : ""}`}
                      weight="bold"
                    />
                  </TableCell>
                  <TableCell>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      player.elo_rank === 1 ? 'rank-1' : player.elo_rank === 2 ? 'rank-2' : player.elo_rank === 3 ? 'rank-3' : 'bg-muted text-muted-foreground'
                    }`}>{player.elo_rank}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10 border border-border">
                        <AvatarImage src={player.photo_url} alt={player.name} />
                        <AvatarFallback className="bg-muted text-muted-foreground text-sm">{getInitials(player.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{player.name}</p>
                        {player.is_inactive && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-chart-3/15 text-chart-3"
                            title={`${player.days_inactive} days since last match · -${player.inactivity_decay_applied || 0} ELO from inactivity`}
                            data-testid={`inactive-chip-${player.id}`}
                          >
                            <Clock className="w-2.5 h-2.5" weight="bold" />
                            inactive {player.days_inactive}d
                            {player.inactivity_decay_applied > 0 && (
                              <span className="ml-0.5 text-destructive">−{player.inactivity_decay_applied}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <span className={`font-heading font-bold text-lg ${elo >= 1700 ? 'text-secondary' : elo >= 1500 ? 'text-primary' : 'text-muted-foreground'}`}>{elo}</span>
                      <EloDeltaArrow delta={player.elo_delta} />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">#{player.ranking}</TableCell>
                  <TableCell className="text-center">{player.matches_played}</TableCell>
                  <TableCell className="text-center">
                    {player.win_streak > 0 ? (
                      <Badge variant="outline" className="text-primary border-primary/30">{player.win_streak}W</Badge>
                    ) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={winRate >= 50 ? 'text-primary' : 'text-muted-foreground'}>{winRate}%</span>
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <RecentEloMatchesRow playerId={player.id} colSpan={8} />
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const MatchPointsTable = ({ players }) => (
    <Table>
      <TableHeader>
        <TableRow className="border-border hover:bg-transparent">
          <TableHead className="w-16 text-muted-foreground">#</TableHead>
          <TableHead className="text-muted-foreground">Player</TableHead>
          <TableHead className="text-muted-foreground">Tier</TableHead>
          <TableHead className="text-right text-muted-foreground">Match Points</TableHead>
          <TableHead className="text-right text-muted-foreground">Club Rank</TableHead>
          <TableHead className="text-center text-muted-foreground">Played</TableHead>
          <TableHead className="text-center text-muted-foreground">Won</TableHead>
          <TableHead className="text-right text-muted-foreground">Win %</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {players.map((player) => {
          const winRate = player.matches_played > 0 ? ((player.matches_won / player.matches_played) * 100).toFixed(1) : 0;
          const mp = player.match_points_total || 0;
          return (
            <TableRow key={player.id} className="border-border hover:bg-white/5" data-testid={`mp-ranking-row-${player.mp_rank}`}>
              <TableCell>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  player.mp_rank === 1 ? 'rank-1' : player.mp_rank === 2 ? 'rank-2' : player.mp_rank === 3 ? 'rank-3' : 'bg-muted text-muted-foreground'
                }`}>{player.mp_rank}</div>
              </TableCell>
              <TableCell>
                <Link to={`/players/${player.id}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarImage src={player.photo_url} alt={player.name} />
                    <AvatarFallback className="bg-muted text-muted-foreground text-sm">{getInitials(player.name)}</AvatarFallback>
                  </Avatar>
                  <p className="font-medium">{player.name}</p>
                </Link>
              </TableCell>
              <TableCell>
                <Badge className={player.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}>
                  {player.tier === 'premier' ? 'Premier' : 'Challenger'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <span className="font-heading font-bold text-lg text-primary">{mp}</span>
              </TableCell>
              <TableCell className="text-right text-muted-foreground">#{player.ranking}</TableCell>
              <TableCell className="text-center">{player.matches_played || 0}</TableCell>
              <TableCell className="text-center font-medium">{player.matches_won || 0}</TableCell>
              <TableCell className="text-right">
                <span className={winRate >= 50 ? 'text-primary' : 'text-muted-foreground'}>{winRate}%</span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <motion.div initial={PAGE_INITIAL} animate={PAGE_ANIMATE} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold flex items-center gap-3">
            <Trophy className="w-7 h-7 sm:w-8 sm:h-8 text-primary" weight="fill" />
            Club Rankings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Annadil Burhani DSM Snooker Section — 2-Year Rolling Merit Order
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1">
            {user?.is_admin && (
              <>
                <Button variant="outline" size="sm" onClick={handleExportPDF} className="border-border" data-testid="export-pdf-btn">
                  <FilePdf className="w-4 h-4 mr-1" />PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportCSV} className="border-border" data-testid="export-csv-btn">
                  <FileCsv className="w-4 h-4 mr-1" />CSV
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={handleWhatsAppShare} className="border-border text-chart-3 hover:text-chart-3" data-testid="share-whatsapp-btn">
              <WhatsappLogo className="w-4 h-4 mr-1" weight="fill" />Share
            </Button>
          </div>
          <div className="relative w-full md:w-64">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search players..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-card border-border"
              data-testid="search-rankings"
            />
          </div>
        </div>
      </div>

      {/* Rankings Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList className="bg-muted/50 w-full overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="all" data-testid="tab-all-rankings" className="text-xs sm:text-sm">All</TabsTrigger>
          <TabsTrigger value="match-points" data-testid="tab-match-points" className="text-xs sm:text-sm">
            <Target className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 text-primary" weight="fill" />
            Points
          </TabsTrigger>
          <TabsTrigger value="elo" data-testid="tab-elo-rankings" className="text-xs sm:text-sm">
            <Lightning className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 text-chart-3" weight="fill" />
            ELO
          </TabsTrigger>
          <TabsTrigger value="premier" data-testid="tab-premier-rankings" className="text-xs sm:text-sm">
            <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 text-secondary" weight="fill" />
            Premier
          </TabsTrigger>
          <TabsTrigger value="challenger" data-testid="tab-challenger-rankings" className="text-xs sm:text-sm">Challenger</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <RankingTable players={searchTerm ? filteredRankings : sortedAllRankings} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="match-points">
          <Card className="bg-card border-border border-l-4 border-l-primary overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" weight="fill" />
                Match Points Leaderboard
                <span className="text-sm font-normal text-muted-foreground ml-2">Points from individual match performance</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <MatchPointsTable players={matchPointsLeaderboard} />
              </div>
              {matchPointsLeaderboard.length > 0 && matchPointsLeaderboard.every(p => !p.match_points_total) && (
                <div className="px-6 py-4 text-center text-sm text-muted-foreground border-t border-border">
                  Match points will be awarded as tournament matches are played. Win=20pts base, Loss=5pts base, with ranking multipliers, handicap adjustments, and frame bonuses.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="elo">
          <Card className="bg-card border-border border-l-4 border-l-chart-3 overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Lightning className="w-5 h-5 text-chart-3" weight="fill" />
                ELO Power Rankings
                <span className="text-sm font-normal text-muted-foreground ml-2">Strength-based rating system</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="How ELO is calculated"
                      className="ml-auto inline-flex items-center justify-center w-6 h-6 rounded-full border border-border text-muted-foreground hover:text-chart-3 hover:border-chart-3 transition-colors"
                      data-testid="elo-info-tooltip-trigger"
                    >
                      <Info className="w-3.5 h-3.5" weight="bold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 text-xs leading-relaxed" align="end" data-testid="elo-info-tooltip-content">
                    <p className="font-semibold text-sm mb-2">How ELO moves after a match</p>
                    <p className="mb-2 text-muted-foreground">
                      You gain more when you beat a <span className="text-primary">stronger</span> opponent
                      and less when you beat a <span className="text-destructive">much lower</span> one —
                      that's why a top-ranked player can win and still only gain a few points.
                    </p>
                    <p className="mb-2 text-muted-foreground">
                      Every win earns a <span className="text-chart-3 font-semibold">minimum of ~+2.4 ELO</span>
                      (5% of K-factor × frame-margin bonus) so no real match ever rounds to zero.
                    </p>
                    <p className="text-muted-foreground">
                      Dominant wins (e.g. 3-0) get a slight margin-of-victory boost.
                      New players (&lt;10 matches) swing faster while their rating settles.
                    </p>
                  </PopoverContent>
                </Popover>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <EloTable players={eloRankings} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="premier">
          <Card className="bg-card border-border border-l-4 border-l-secondary overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Crown className="w-5 h-5 text-secondary" weight="fill" />
                Premier Group
                <span className="text-sm font-normal text-muted-foreground ml-2">Top 12 Players</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <RankingTable players={sortedPremierRankings} showTier={false} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="challenger">
          <Card className="bg-card border-border overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">
                Challenger Group
                <span className="text-sm font-normal text-muted-foreground ml-2">Players ranked 13 and below</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <RankingTable players={sortedChallengerRankings} showTier={false} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
